import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ContractDetailsLayout from './ContractDetailsLayout';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import UnderDevelopment from 'components/utils/UnderDevelopment';
import { SubContract } from './SubContract';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { ContractDetailsGlobal, ContractType } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { GenericLoading } from 'components/ui/Loader';
import { useTimelineNotifications } from 'shared/signalR/hooks/useTimelineNotifications';

const ContractDetails: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common])
  const { id } = useParams<{ id: string }>();

  const [groupContractTab, setGroupContractTab] = useState<number | string>(0);
  const [contractDetails, setContractDetails] = useState<ContractDetailsGlobal>()
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchContractDetails = async (contractId: string) => {
      setIsLoading(true);
      try {

        const response = await contractsAPI.getGlobalContractDetails({ contractId })
        if (response)
          setContractDetails(response)
      } catch (error) {
        console.error('Error fetching contract :', error);
      } finally {
        setIsLoading(false);
      }
    }
    if (id)
      fetchContractDetails(id)
  }, [id])

  // SignalR : écoute des mises à jour du nombre d'historiques non lus
  const handleUnreadCountUpdate = useCallback((notification: { unreadCount: number }) => {
    setContractDetails(prev => prev ? { ...prev, unreadHistoryCount: notification.unreadCount } : prev);
  }, []);

  const { memoizedConnect, memoizedDisconnect } = useTimelineNotifications(
    contractDetails?.riskId,
    handleUnreadCountUpdate
  );

  useEffect(() => {
    if (contractDetails?.riskId) {
      memoizedConnect();
    }
    return () => {
      memoizedDisconnect();
    };
  }, [contractDetails?.riskId, memoizedConnect, memoizedDisconnect]);

  const showSubContract = useMemo(() => {
    if (contractDetails)
      return contractDetails.contractType === ContractType.ALIMONY || contractDetails?.contractType === ContractType.INDIVIDUAL
    return false
  }, [contractDetails])

  const showGroupContract = useMemo(() => {
    if (contractDetails)
      return contractDetails.contractType === ContractType.GROUP
    return false
  }, [contractDetails])

  const renderGroupContractTabs = useMemo(() => {
    return <GenericTabs defaultTab={groupContractTab} onChange={setGroupContractTab}>
      <GenericTabs.Item
        label={t("CONTRACT.LABELS.SUB_CONTRACTS_TITLE")}
        value={1}
      />
      <GenericTabs.Content value={1}>
        <UnderDevelopment featureName='ListAliments' />
      </GenericTabs.Content>
      <GenericTabs.Item
        label={t("CONTRACT.LABELS.CONTRACT_DOCUMENTS")}
        value={2}
      />
      <GenericTabs.Content value={2}>
        <UnderDevelopment featureName='DocumentContractuel' />
      </GenericTabs.Content>
      <GenericTabs.Item
        label={t("COMMON.HISTORY")}
        value={3}
      />
      <GenericTabs.Content value={3}>
        <UnderDevelopment featureName='History' />
      </GenericTabs.Content>
    </GenericTabs>
  }, [t, groupContractTab])

  if (isLoading) {
    return <GenericLoading loading isFullPage />
  }

  if (!contractDetails) {
    return <></>
  }

  return (
    <SimpleDashboardContent>
      <DashboardHeader />
      <ContractDetailsLayout contractDetails={contractDetails}>
        {
          showSubContract && id &&
          <SubContract contractDetails={contractDetails} contractId={id} />
        }
        {
          showGroupContract &&
          renderGroupContractTabs
        }
      </ContractDetailsLayout>
    </SimpleDashboardContent>
  );
};

export default ContractDetails
